/**
 * DraggableSection - encart Dashboard re-ordonnable.
 * Le handle (grip 6 points) en haut a gauche permet de drag/drop.
 * L'ordre est persiste dans localStorage par `storageKey`.
 */
import React, { useState, useCallback } from 'react';

export const useDashboardOrder = (storageKey, defaultOrder) => {
  const [order, setOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Verifie que tous les keys par defaut sont presents (resilient aux ajouts)
          const missing = defaultOrder.filter(k => !parsed.includes(k));
          const valid = parsed.filter(k => defaultOrder.includes(k));
          return [...valid, ...missing];
        }
      }
    } catch {}
    return defaultOrder;
  });

  const moveBefore = useCallback((fromKey, toKey) => {
    setOrder(prev => {
      if (!fromKey || fromKey === toKey) return prev;
      const next = prev.filter(k => k !== fromKey);
      const toIdx = next.indexOf(toKey);
      if (toIdx < 0) return prev;
      next.splice(toIdx, 0, fromKey);
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [storageKey]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch {}
    setOrder(defaultOrder);
  }, [storageKey, defaultOrder]);

  return { order, moveBefore, reset };
};

export const DraggableSection = ({ keyId, onMove, children, dragging, setDragging }) => {
  const [isOver, setIsOver] = useState(false);

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', keyId);
    e.dataTransfer.effectAllowed = 'move';
    setDragging(keyId);
  };

  const handleDragEnd = () => {
    setDragging(null);
    setIsOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragging && dragging !== keyId) setIsOver(true);
  };

  const handleDragLeave = () => setIsOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    const fromKey = e.dataTransfer.getData('text/plain');
    if (fromKey) onMove(fromKey, keyId);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative group transition-all rounded-xl ${
        isOver ? 'ring-2 ring-[#008A8D] ring-offset-2 scale-[1.005]' : ''
      } ${dragging === keyId ? 'opacity-50' : ''}`}
      data-testid={`dash-section-${keyId}`}
    >
      <div
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className="absolute -left-3 top-4 z-10 cursor-grab active:cursor-grabbing px-1.5 py-2 bg-white shadow-md border border-gray-200 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        title="Glisser pour reorganiser"
        data-testid={`dash-handle-${keyId}`}
      >
        <svg className="w-3 h-4 text-gray-400" fill="currentColor" viewBox="0 0 6 16" aria-hidden="true">
          <circle cx="1.5" cy="2" r="1.2" />
          <circle cx="1.5" cy="8" r="1.2" />
          <circle cx="1.5" cy="14" r="1.2" />
          <circle cx="4.5" cy="2" r="1.2" />
          <circle cx="4.5" cy="8" r="1.2" />
          <circle cx="4.5" cy="14" r="1.2" />
        </svg>
      </div>
      {children}
    </div>
  );
};

export default DraggableSection;
